import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { iif, map, Observable, of, startWith, Subject, Subscription, switchMap, take } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  TagTheme,
} from '@fcom/ui-components';
import { HeaderItem, RowDataItem } from '@fcom/ui-components/components/table/table.component';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { unsubscribe } from '@fcom/core/utils';
import { SelectOption } from '@fcom/common';
import { profile } from '@fcom/core/selectors';
import { CorporateUserRole } from '@fcom/core-api/login';
import { ConfigService } from '@fcom/core';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { Company, CompanyDetailsForm, ContactPersonForm, CorporateUser } from '@fcom/common/interfaces';

import { SafTermsComponent } from '../widgets/saf-widget/saf-terms.component';
import { CorporateFeatureState } from '../../store/constants';
import { company } from '../../store/selectors/company.selector';
import { CompanyActions } from '../../store/actions';

enum DataType {
  TEXT,
  DATE,
}

interface DataInterface {
  label?: string;
  value?: string;
  type?: DataType;
}

interface PartnerServiceRow {
  tagTheme: TagTheme;
  tagText: string;
  tagDisabled?: boolean;
  linkText?: string;
}

export enum EditFormType {
  COMPANY_DETAILS,
  CONTACT_PERSONS,
}

@Component({
  selector: 'fin-company-details',
  templateUrl: './company-details.component.html',
  styleUrls: ['./company-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDetailsComponent implements OnInit, OnDestroy {
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly DataType = DataType;
  protected readonly EditFormType = EditFormType;
  protected readonly IconButtonSize = IconButtonSize;
  protected readonly IconButtonTheme = IconButtonTheme;
  protected readonly IconPosition = IconPosition;
  protected readonly LoaderTheme = LoaderTheme;
  protected readonly ModalButtons = ModalButtons;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;

  @ViewChild('safTerms') safTerms: SafTermsComponent;

  company$: Observable<Company>;
  users$: Observable<CorporateUser[]>;
  isAdmin$: Observable<boolean>;

  companyDetailsHeaders: HeaderItem[] = [{ key: 'label' }, { key: 'value' }];
  companyDetailsData$: Observable<RowDataItem<DataInterface>[]>;
  agreementDetailsHeaders: HeaderItem[] = [{ key: 'start' }, { key: 'end' }];
  agreementDetailsData$: Observable<RowDataItem<DataInterface>[]>;

  choooseRow$: Observable<PartnerServiceRow>;
  hasActivatedSAF$: Observable<boolean>;
  termsUpdated$ = new Subject();

  updating = false;
  safLoading = false;
  editFormType: EditFormType;
  openCompanyDetails = false;
  openContactDetails = false;

  form: FormGroup<CompanyDetailsForm>;
  contactPersonForm: FormGroup<ContactPersonForm>;

  primaryContacts$: Observable<SelectOption[]>;
  secondaryContacts$: Observable<SelectOption[]>;

  subscriptions = new Subscription();

  constructor(
    private corporateService: CorporateService,
    private languageService: LanguageService,
    protected configService: ConfigService,
    private store$: Store<CorporateFeatureState>,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.store$.dispatch(CompanyActions.fetchCompany());

    this.company$ = this.store$.pipe(
      company(),
      tap(() => (this.updating = false)),
      finShare()
    );

    this.companyDetailsData$ = this.company$.pipe(
      filter(Boolean),
      switchMap((c) => this.mapCompanyDetails(c))
    );
    this.agreementDetailsData$ = this.company$.pipe(
      filter(Boolean),
      map((c) => this.mapAgreementDetails(c))
    );
    this.users$ = this.corporateService.getUsers().pipe(finShare());
    this.isAdmin$ = this.store$.pipe(
      profile(),
      map((p) => p.corporate.userRole === CorporateUserRole.ADMIN),
      finShare()
    );
    this.hasActivatedSAF$ = this.termsUpdated$.pipe(
      startWith(undefined),
      switchMap(() => this.corporateService.hasActivatedSAF()),
      finShare()
    );

    const choooseTagForAdmin$ = this.hasActivatedSAF$.pipe(
      map((hasActivated) =>
        hasActivated
          ? {
              tagTheme: TagTheme.SUSTAINABILITY,
              tagText: 'CORPORATE.corporatePortal.corporateDetails.chooose.tag.available',
              linkText: 'CORPORATE.corporatePortal.corporateDetails.chooose.viewEmissions',
            }
          : {
              tagTheme: TagTheme.DEFAULT,
              tagText: 'CORPORATE.corporatePortal.corporateDetails.chooose.tag.notActivated',
              linkText: 'CORPORATE.corporatePortal.corporateDetails.chooose.activate',
            }
      )
    );
    const choooseTagForNonAdmin$ = of({
      tagTheme: TagTheme.DEFAULT,
      tagText: 'CORPORATE.corporatePortal.corporateDetails.chooose.tag.notAdmin',
      tagDisabled: true,
    });

    this.choooseRow$ = this.isAdmin$.pipe(
      switchMap((isAdmin) => {
        return iif(() => isAdmin, choooseTagForAdmin$, choooseTagForNonAdmin$);
      })
    );

    this.subscriptions.add(
      this.company$.pipe(filter(Boolean), take(1)).subscribe((c) => {
        this.form = this.fb.group({
          address: [c.street, Validators.required],
          postalCode: [c.zipcode, Validators.required],
          city: [c.city, Validators.required],
        });
      })
    );
  }

  editCompanyDetails(): void {
    this.openCompanyDetails = true;
  }

  editContactPersons(): void {
    this.subscriptions.add(
      this.company$.subscribe((c) => {
        this.contactPersonForm = this.fb.group({
          primaryContact: [c.primaryAdmin?.id, Validators.required],
          secondaryContact: [c.secondaryAdmin?.id],
        });
      })
    );
    this.setContactOptions();

    this.openContactDetails = true;
  }

  updateCompany(formType: EditFormType): void {
    this.editFormType = formType;
    this.updating = true;
    const payload = {
      street: this.form.get('address').value,
      zipcode: this.form.get('postalCode').value,
      city: this.form.get('city').value,
    };
    this.store$.dispatch(CompanyActions.updateCompany({ payload }));
  }

  updateContactPersons(): void {
    this.editFormType = EditFormType.CONTACT_PERSONS;
    this.updating = true;
    const payload = {
      primaryContact: this.contactPersonForm.get('primaryContact').value,
      secondaryContact: this.contactPersonForm.get('secondaryContact').value,
    };
    this.store$.dispatch(CompanyActions.updateContactPersons({ payload }));
  }

  setContactOptions(): void {
    const admins$ = this.users$.pipe(
      map((users) => {
        return users.filter((u) => u.role === CorporateUserRole.ADMIN);
      }),
      finShare()
    );
    this.primaryContacts$ = admins$.pipe(
      map((admins) => {
        return admins
          .filter((a) => a.siebelId !== this.contactPersonForm?.get('secondaryContact').value)
          .map((a) => ({ value: a.siebelId, name: `${a.firstname} ${a.lastname}` }));
      })
    );
    this.secondaryContacts$ = admins$.pipe(
      map((admins) => {
        return admins
          .filter((a) => a.siebelId !== this.contactPersonForm?.get('primaryContact').value)
          .map((a) => ({ value: a.siebelId, name: `${a.firstname} ${a.lastname}` }));
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  goToChooose(): void {
    this.safLoading = true;
    this.safTerms.checkTermsAndRedirect();
  }

  termsUpdated(): void {
    this.termsUpdated$.next(undefined);
    this.safLoading = false;
  }

  private mapCompanyDetails({
    name,
    clientId,
    businessId,
    street,
    zipcode,
    city,
    country,
  }): Observable<RowDataItem<DataInterface>[]> {
    return this.languageService.translate('countries').pipe(
      map((countries) => {
        return this.mapToRowData([
          { label: 'name', value: name },
          { label: 'corporateNumber', value: clientId },
          { label: 'businessId', value: businessId },
          { label: 'address', value: street },
          { label: 'postalCode', value: zipcode },
          { label: 'city', value: city },
          { label: 'country', value: country ? countries[country.toLowerCase()] : undefined },
        ]);
      })
    );
  }

  private mapAgreementDetails({ agreement1, agreement2 }): RowDataItem<DataInterface>[] {
    return [agreement1, agreement2]
      .map((agreement) => {
        if (!agreement?.agreementType) {
          return null;
        }
        return {
          start: {
            data: {
              value: agreement.agreementStart,
              label: 'start',
              type: DataType.DATE,
            },
            options: {
              class: 'fixed-240',
            },
          },
          end: {
            data: {
              value: agreement.agreementEnd,
              label: 'end',
              type: DataType.DATE,
            },
          },
        };
      })
      .filter(Boolean);
  }

  private mapToRowData(data: { label: string; value: string; type?: DataType }[]): RowDataItem<DataInterface>[] {
    return data.map((row) => ({
      label: { data: { label: row.label } },
      value: { data: { value: row.value, type: row.type || DataType.TEXT }, options: { class: 'bold-type' } },
    }));
  }
}
